import { createSelector } from 'reselect'
import { State } from '../../reducers/rootReducer'
import equipmentGroupsSelector from '../equipments/equipmentGroupsSelector'
import { GlobalEquipmentSearchResult, EquipmentGlobalSearchResultType } from '../../../entities/GlobalEquipmentSearchResult'

export const globalSearchQuerySelector = (state: State): string => {
    const globalSearch = state.globalEquipmentSearch as { query: string } | undefined
    return globalSearch?.query || ''
}

const globalEquipmentSearchResultsSelector = createSelector(
    equipmentGroupsSelector,
    globalSearchQuerySelector,
    (equipmentGroups, query): GlobalEquipmentSearchResult[] => {
        // require minimum 2 characters
        if (query.length < 2) {
            return []
        }

        const searchResults: GlobalEquipmentSearchResult[] = []
        const queryLower = query.toLowerCase()

        const matchesQuery = (text: string | undefined): boolean =>
            !!text && text.toLowerCase().includes(queryLower)

        equipmentGroups.forEach(equipmentGroup => {
            if (!equipmentGroup.equipmentFamilies) {
                return
            }

            equipmentGroup.equipmentFamilies.forEach(equipmentFamily => {
                if (!equipmentFamily.equipments) {
                    return
                }

                equipmentFamily.equipments.forEach(equipment => {
                    // skip hidden/stealth equipment
                    if (equipment.hidden || equipment.stealth) {
                        return
                    }

                    const idMatches = matchesQuery(equipment.evaKey)
                    const nameMatches = matchesQuery(equipment.name)

                    if (idMatches || nameMatches) {
                        searchResults.push({
                            id: `${equipmentGroup.id}_${equipmentFamily.id}_${equipment.id}`,
                            equipmentId: equipment.id,
                            equipmentEvaKey: equipment.evaKey,
                            equipmentName: equipment.name,
                            equipmentGroupId: equipmentGroup.id,
                            equipmentFamilyName: equipmentFamily.name,
                            matchedText: idMatches ? equipment.evaKey : equipment.name,
                            type: EquipmentGlobalSearchResultType.EQUIPMENT,
                        })
                    }

                    // search package content
                    if (equipment.isPackage && equipment.content) {
                        equipment.content.forEach(contentItem => {
                            const contentIdMatches = matchesQuery(contentItem.id?.slice(-3))
                            const contentNameMatches = matchesQuery(contentItem.name)

                            if (contentIdMatches || contentNameMatches) {
                                searchResults.push({
                                    id: `${equipmentGroup.id}_${equipmentFamily.id}_${equipment.id}_${contentItem.id}`,
                                    equipmentId: equipment.id,
                                    equipmentEvaKey: contentItem.id?.slice(-3),
                                    equipmentName: contentItem.name,
                                    equipmentGroupId: equipmentGroup.id,
                                    equipmentFamilyName: equipmentFamily.name,
                                    matchedText: contentIdMatches ? contentItem.id : contentItem.name,
                                    type: EquipmentGlobalSearchResultType.PACKAGE_CONTENT,
                                    packageParentId: equipment.evaKey,
                                    packageParentName: equipment.name,
                                })
                            }
                        })
                    }
                })
            })
        })

        return searchResults
            .sort((a, b) => {
                // 1. priority of exact matches over partial matches
                const aExact = a.matchedText.toLowerCase() === queryLower
                const bExact = b.matchedText.toLowerCase() === queryLower
                if (aExact && !bExact) {
                    return -1
                }
                if (!aExact && bExact) {
                    return 1
                }

                // 2. priority of ID matches over name matches (dealer priority!)
                const aMatchedId = a.matchedText === a.equipmentEvaKey
                const bMatchedId = b.matchedText === b.equipmentEvaKey
                if (aMatchedId && !bMatchedId) {
                    return -1
                }
                if (!aMatchedId && bMatchedId) {
                    return 1
                }

                // 3. priority of equipment over package content
                if (a.type === EquipmentGlobalSearchResultType.EQUIPMENT && b.type === EquipmentGlobalSearchResultType.PACKAGE_CONTENT) {
                    return -1
                }
                if (a.type === EquipmentGlobalSearchResultType.PACKAGE_CONTENT && b.type === EquipmentGlobalSearchResultType.EQUIPMENT) {
                    return 1
                }

                // 4. alphabetically sorted
                return a.equipmentName.localeCompare(b.equipmentName)
            })
    },
)

export default globalEquipmentSearchResultsSelector
